





























































































































































































































































































































































import moment from "moment";
import momentTimezone from "moment-timezone";
import { Component, Vue } from "vue-property-decorator";
import {
  formatDateReadable,
  formatTimeReadable,
  dateToDay
} from "../../utils/time";
import {
  Resource,
  Service,
  ServicePackage,
  BookingRequest
} from "../../api/types";
import { ShopsModule } from "@/store/modules/shops";
import { BookingsModule } from "@/store/modules/bookings";
import TheNavbar from "@/components/TheNavbar.vue";
import SbSpinner from "@/components/Spinner.vue";
import SbModal from "@/components/Modal.vue";
import ShopInfo from "./ShopInfo.vue";
import ShopMenuItemService from "./ShopMenuItemService.vue";
import ShopMenuConfirmModal from "./ShopMenuConfirmModal.vue";

const NUM_DATE_OPTIONS_INCREMENT_AMOUNT = 6;

const formatServiceDuration = (service: Service): string => {
  const { packages } = service;
  const packagesValues = Object.values(packages);

  if (packagesValues.length === 1) {
    const { duration } = packagesValues[0];
    return `${duration} min`;
  }

  const durations = packagesValues.map(pack => pack.duration);
  const minDuration = Math.min(...durations);
  const maxDuration = Math.max(...durations);
  if (maxDuration > minDuration) return `${minDuration} - $${maxDuration} min`;
  return `${maxDuration} min`;
};

const formatServicePrice = (service: Service): string => {
  const { packages } = service;
  const packagesValues = Object.values(packages);

  if (packagesValues.length === 1) {
    const { price } = packagesValues[0];
    return `€${price}`;
  }

  const prices = packagesValues.map(pack => pack.price);
  const minPrice = Math.min(...prices);
  return `From €${minPrice}`;
};

@Component({
  name: "Shop",
  components: {
    SbModal,
    SbSpinner,
    ShopMenuItemService,
    ShopMenuConfirmModal,
    ShopInfo,
    TheNavbar
  },
  filters: {
    formatDateReadable,
    formatTimeReadable,
    formatServiceDuration,
    formatServicePrice,
    dateToDay
  }
})
export default class extends Vue {
  private NEW_LINE_ENCODED = "%0D%0A";

  private selectedService: Service | null = null;
  private selectedServicePackage: ServicePackage | null = null;
  private selectedResource: Resource | null = null;
  private selectedDate: string | null = null;
  private selectedTime: string | null = null;
  private maxNumDateOptions = NUM_DATE_OPTIONS_INCREMENT_AMOUNT;
  private isLoadingBookingsAvailability = false;
  private bookingRequest: BookingRequest | null = null;
  private isConfirmModalOpen = false;
  private isLoading = false;
  private serviceContactModalOpen = false;

  get shop() {
    return ShopsModule.shop;
  }

  get shopAggregations() {
    return ShopsModule.shopAggregations;
  }

  get bookingsAvailability() {
    return BookingsModule.bookingsAvailability;
  }

  get accentColor() {
    return this.shop.colorTheme;
  }

  get shopStyles() {
    return {
      "--sb-shop-accent-color": this.accentColor
    };
  }

  scrollTo(element: string) {
    this.$scrollTo(element, 500, { offset: -30 });
  }

  showLoadingIndicator() {
    this.isLoading = true;
  }

  hideLoadingIndicator() {
    this.isLoading = false;
  }

  async loadShopData() {
    const { shopId } = this.$route.params;
    try {
      await ShopsModule.getShop(shopId);
      await ShopsModule.getShopAggregations(shopId);
    } catch (e) {
      console.error(e);
    }
  }

  get servicesOptions(): Service[] {
    const { services } = this.shopAggregations;
    if (!services) return [];
    const servicesList: Service[] = Object.values(services);
    return servicesList
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(svc => svc.availableForOnlineBookings);
  }

  get servicePackagesOptions(): ServicePackage[] {
    const { selectedService } = this;
    if (!selectedService) return [];
    const { packages, packagesDisplayOrder } = selectedService;
    if (packagesDisplayOrder.length === 1) return [];
    return packagesDisplayOrder.map(
      servicePackageId => packages[servicePackageId]
    );
  }

  get resourcesOptions(): Resource[] {
    const { selectedService } = this;

    if (!selectedService) return [];

    const { resources } = this.shopAggregations;
    const { resourceAnyMode } = this.shop;

    if (!resources) return [];

    const selectedServiceResources = selectedService.resources.map(
      resourceId => resources[resourceId]
    );
    const anyResource = {
      description: "",
      image: this.shop.logoDownloadUrl,
      name: "Any",
      availableForOnlineBookings: true,
      _id: "Any"
    };
    if (resourceAnyMode === "On") {
      selectedServiceResources.unshift(anyResource);

      return selectedServiceResources.filter(
        resource => resource.availableForOnlineBookings
      );
    } else if (resourceAnyMode === "Only Allow Any") {
      this.selectedResource = anyResource;
      this.loadBookingsAvailability();
      return [anyResource];
    } else {
      return selectedServiceResources.filter(
        resource => resource.availableForOnlineBookings
      );
    }
  }

  get availableDates(): string[] {
    return Object.keys(this.bookingsAvailability);
  }

  get dateOptions(): string[] {
    const { availableDates, maxNumDateOptions } = this;
    const currentIrishTime = momentTimezone()
      .tz("GMT", true)
      .format("YYYY-MM-DD");
    const nonHistoricDates = availableDates.filter(
      date => date >= currentIrishTime
    );
    const numDateOptions = Math.min(nonHistoricDates.length, maxNumDateOptions);
    return nonHistoricDates.slice(0, numDateOptions);
  }

  get isSelectedDateToday(): boolean {
    const { selectedDate } = this;

    if (!selectedDate) return false;

    const date = moment()
      .tz("GMT")
      .format("YYYY-MM-DD");

    return date === selectedDate;
  }

  get timeOptions(): string[] {
    const { selectedDate, bookingsAvailability } = this;

    if (!selectedDate) return [];

    const timeOptions = bookingsAvailability[selectedDate];

    if (!timeOptions.length) return [];

    if (!this.isSelectedDateToday) {
      return timeOptions;
    }

    // remove this filthy patch,
    // Timeslots coming back from the backend
    // should not contain times in the past.
    const currentIrishTime = momentTimezone()
      .tz("GMT", true)
      .format("HH:mm");

    return timeOptions.filter(time => time > currentIrishTime);
  }

  get timeOptionsInMorning(): string[] {
    return this.timeOptions.filter(time => time < "12:00");
  }

  get timeOptionsInAfternoon(): string[] {
    return this.timeOptions.filter(time => time >= "12:00" && time < "18:00");
  }

  get timeOptionsInEvening(): string[] {
    return this.timeOptions.filter(time => time >= "18:00");
  }

  async loadBookingsAvailability() {
    this.isLoadingBookingsAvailability = true;
    const { selectedService, selectedResource } = this;
    if (!selectedService) return;
    if (!selectedResource) return;
    try {
      await BookingsModule.getBookingsAvailability({
        shopId: this.shop._id,
        serviceId: selectedService._id,
        resourceId: selectedResource._id,
        storeAgg: this.shopAggregations
      });
    } catch (e) {
      // TODO
      console.log(e);
    } finally {
      this.isLoadingBookingsAvailability = false;
    }
  }

  onServiceClick(service: Service) {
    if (this.selectedService === service) return;

    this.selectedService = service;

    if (service.requiresContact) {
      this.serviceContactModalOpen = true;
      return;
    }

    const hasSinglePackage = service.packagesDisplayOrder.length === 1;

    if (hasSinglePackage) {
      this.selectFirstServicePackage(service);
    } else {
      this.deselectServicePackage();
      this.$nextTick(() => this.scrollTo("#servicePackageSection"));
    }
  }

  closeServiceRequiresContactModal() {
    this.selectedService = null;
    this.serviceContactModalOpen = false;
  }

  selectFirstServicePackage(service: Service) {
    const selectedServicePackageId = service.packagesDisplayOrder[0];
    const selectedServicePackage = service.packages[selectedServicePackageId];
    this.onServicePackageClick(selectedServicePackage);
  }

  onServicePackageClick(servicePackage: ServicePackage) {
    if (this.selectedServicePackage === servicePackage) return;
    this.selectedServicePackage = servicePackage;
    this.deselectResource();
    this.$nextTick(() => this.scrollTo("#resourceSection"));
  }

  async onResourceClick(resource: Resource) {
    if (this.selectedResource === resource) return;
    this.selectedResource = resource;
    this.deselectDate();
    await this.loadBookingsAvailability();
    this.$nextTick(() => this.scrollTo("#dateSection"));
  }

  onDateClick(date: string) {
    if (this.selectedDate === date) return;
    this.selectedDate = date;
    this.deselectTime();
    this.$nextTick(() => this.scrollTo("#timeSection"));
  }

  onTimeClick(time: string) {
    this.selectedTime = time;
    this.loadConfirmModal();
  }

  incrementMaxNumDateOptions() {
    this.maxNumDateOptions =
      this.maxNumDateOptions + NUM_DATE_OPTIONS_INCREMENT_AMOUNT;
  }

  deselectService() {
    this.selectedService = null;
    this.deselectServicePackage();
  }

  deselectServicePackage() {
    this.selectedServicePackage = null;
    this.deselectResource();
  }

  deselectResource() {
    this.selectedResource = null;
    this.deselectDate();
  }

  deselectDate() {
    this.selectedDate = null;
    this.deselectTime();
  }

  deselectTime() {
    this.selectedTime = null;
  }

  loadConfirmModal() {
    const { shopId } = this.$route.params;
    const {
      selectedService,
      selectedServicePackage,
      selectedResource,
      selectedDate,
      selectedTime
    } = this;

    if (!selectedService) return;
    if (!selectedServicePackage) return;
    if (!selectedResource) return;
    if (!selectedDate) return;
    if (!selectedTime) return;
    const startDate = moment.tz(selectedDate, "YYYY-MM-DD", "GMT");
    const [hour, minute] = selectedTime.split(":");
    const hourMinute = {
      hour: parseInt(hour),
      minute: parseInt(minute)
    };
    const start = moment(startDate)
      .set(hourMinute)
      .format();
    const end = moment(start)
      .add(selectedService.duration, "minute")
      .tz("GMT")
      .format();
    const endDate = moment(end)
      .tz("GMT")
      .format("YYYY-MM-DD");
    const endTime = moment(end)
      .tz("GMT")
      .format("HH:mm");

    this.bookingRequest = {
      storeId: shopId,
      serviceId: selectedService._id,
      servicePackageId: selectedServicePackage._id,
      resourceId: selectedResource._id,
      customer: {
        name: "",
        emailAddress: "",
        phoneNumber: ""
      },
      start,
      startDate: selectedDate,
      startTime: selectedTime,
      end,
      endDate,
      endTime
    };

    this.isConfirmModalOpen = true;
  }

  async created() {
    this.showLoadingIndicator();
    await this.loadShopData();
    setTimeout(this.hideLoadingIndicator, 300);
  }
}
